import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { makeHashi, candidateEdges, edgesCross, hashiWin } from "@/lib/hashi";

export function HashiGame({ size, targetIslands, onWin, resetKey }) {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState(null);
  const [edges, setEdges] = useState([]);
  const [counts, setCounts] = useState([]);

  const start = useCallback(() => {
    const p = makeHashi(size, targetIslands);
    const e = candidateEdges(p.islands, size);
    setPuzzle(p);
    setEdges(e);
    setCounts(new Array(e.length).fill(0));
  }, [size, targetIslands]);

  useEffect(() => { start(); }, [start, resetKey]);

  const solved = useMemo(() => (puzzle && counts.length ? hashiWin(puzzle.islands, edges, counts) : false), [puzzle, edges, counts]);
  useEffect(() => { if (solved) onWin?.(); }, [solved, onWin]);

  const cycleEdge = (idx) => {
    if (solved) return;
    setCounts((cs) => {
      const nc = cs.slice();
      if (nc[idx] === 0) {
        for (let j = 0; j < edges.length; j++) if (j !== idx && nc[j] > 0 && edgesCross(edges[idx], edges[j])) return cs;
        nc[idx] = 1;
      } else if (nc[idx] === 1) nc[idx] = 2;
      else nc[idx] = 0;
      return nc;
    });
  };

  if (!puzzle) return null;
  const B = "min(88vw, 460px)";
  const cell = `calc(${B} / ${size})`;
  const pct = (v) => `${(v * 100) / size}%`;

  return (
    <div className="flex flex-col items-center" data-testid="hashi-game">
      <p className="text-xs text-foreground/45 text-center max-w-md mb-6 leading-relaxed">{t("games.hashiRules")}</p>
      <div className="relative" style={{ width: B, height: B }} data-testid="hashi-board">
        {edges.map((e, idx) => {
          const cnt = counts[idx];
          const horiz = e.orient === "H";
          const style = horiz
            ? { left: pct(Math.min(e.c1, e.c2) + 0.5), top: pct(e.r + 0.5), width: pct(Math.abs(e.c2 - e.c1)), height: cell, transform: "translateY(-50%)" }
            : { left: pct(e.c + 0.5), top: pct(Math.min(e.r1, e.r2) + 0.5), width: cell, height: pct(Math.abs(e.r2 - e.r1)), transform: "translateX(-50%)" };
          return (
            <button
              key={idx}
              data-testid={`hashi-edge-${idx}`}
              onClick={() => cycleEdge(idx)}
              style={{ position: "absolute", ...style }}
              className="flex items-center justify-center z-10"
            >
              <span className={`${horiz ? "h-full w-[2px]" : "w-full h-[2px]"} relative flex ${horiz ? "flex-row" : "flex-col"} items-center justify-center`} style={{ width: horiz ? "100%" : undefined, height: horiz ? undefined : "100%" }}>
                {cnt === 0 ? (
                  <span className={`${horiz ? "w-full h-px" : "h-full w-px"} border-dashed`} style={{ borderTop: horiz ? "1px dashed hsl(30 20% 40% / 0.25)" : undefined, borderLeft: horiz ? undefined : "1px dashed hsl(30 20% 40% / 0.25)" }} />
                ) : (
                  <span className={`flex ${horiz ? "flex-col" : "flex-row"} gap-[3px] ${horiz ? "w-full" : "h-full"}`}>
                    {Array.from({ length: cnt }).map((_, i) => (
                      <span key={i} className="bg-primary" style={horiz ? { height: "2px", width: "100%" } : { width: "2px", height: "100%" }} />
                    ))}
                  </span>
                )}
              </span>
            </button>
          );
        })}
        {puzzle.islands.map((isl) => {
          const deg = edges.reduce((s, e, idx) => s + ((e.a === isl.id || e.b === isl.id) ? counts[idx] : 0), 0);
          const complete = deg === isl.value;
          return (
            <div
              key={isl.id}
              data-testid={`hashi-island-${isl.id}`}
              style={{ position: "absolute", left: pct(isl.c + 0.5), top: pct(isl.r + 0.5), width: `calc(${cell} * 0.66)`, height: `calc(${cell} * 0.66)`, transform: "translate(-50%, -50%)", fontSize: `calc(${cell} * 0.4)` }}
              className={`z-20 rounded-full flex items-center justify-center font-display border-2 pointer-events-none transition-colors
                ${complete ? "bg-primary/25 border-primary text-primary" : "bg-[hsl(24_18%_11%)] border-primary/50 text-foreground/90"}`}
            >
              {isl.value}
            </div>
          );
        })}
      </div>
      <button onClick={start} data-testid="hashi-new-btn" className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/45 hover:text-primary transition-colors">
        <RefreshCw size={13} /> {t("games.newGrid")}
      </button>
    </div>
  );
}
